<script lang="ts">
	import SelectField from '../../components/SelectField.svelte';
	import { Button, Badge, Table, TableHead, TableBody, TableHeadCell, TableBodyRow, TableBodyCell, Search } from 'flowbite-svelte';
	import PageHeader from '../../components/PageHeader.svelte';
	import Panel from '../../components/Panel.svelte';
	import AbstractID from '../../AbstractID';
	import { formatFriendlyDate, formatRelativeDate } from '../../Utilities';
	import site from '../../site';
	import { onMount } from 'svelte';
	import type { AuditLog } from '../../AppTypes';

	type BadgeColor = 'red' | 'yellow' | 'green' | 'indigo' | 'purple' | 'pink' | 'blue' | 'dark' | 'primary' | 'none';

	let logs: AuditLog[] = [];
	let loading = true;
	let error = '';
	let total = 0;
	let page = 1;
	let limit = 25;
	let actionFilter = '';
	let searchQuery = '';

	const actionTypes = [
		{ value: '', name: 'All Actions' },
		{ value: 'USER_REGISTERED', name: 'User Registered' },
		{ value: 'USER_LOGIN', name: 'User Login' },
		{ value: 'ROLE_CHANGED', name: 'Role Changed' },
		{ value: 'SETTINGS_CHANGED', name: 'Settings Changed' },
		{ value: 'USER_UPDATED', name: 'User Updated' },
		{ value: 'PAYMENT_PROVIDER_CREATED', name: 'Payment Provider Created' },
		{ value: 'PAYMENT_PROVIDER_UPDATED', name: 'Payment Provider Updated' },
		{ value: 'PAYMENT_PROVIDER_DEFAULT_CHANGED', name: 'Default Provider Changed' },
		{ value: 'PROVIDER_CREDENTIALS_ROTATED', name: 'Provider Credentials Rotated' },
		{ value: 'CURRENCY_UPDATED', name: 'Currency Updated' },
		{ value: 'REFUND_POLICY_UPDATED', name: 'Refund Policy Updated' },
		{ value: 'EMAIL_PROVIDER_UPDATED', name: 'Email Provider Updated' },
		{ value: 'EMAIL_TEMPLATE_UPDATED', name: 'Email Template Updated' },
		{ value: 'CHECKOUT_STARTED', name: 'Checkout Started' },
		{ value: 'PURCHASE_COMPLETED', name: 'Purchase Completed' },
		{ value: 'SUBSCRIPTION_CREATED', name: 'Subscription Created' },
		{ value: 'SUBSCRIPTION_CHANGED', name: 'Subscription Changed' },
		{ value: 'SUBSCRIPTION_CANCELED', name: 'Subscription Canceled' },
		{ value: 'SUBSCRIPTION_CANCEL_FAILED', name: 'Subscription Cancel Failed' },
		{ value: 'SUBSCRIPTION_RESUMED', name: 'Subscription Resumed' },
		{ value: 'SUBSCRIPTION_TAX_RATE_UPDATED', name: 'Subscription Tax Rate Updated' },
		{ value: 'SELLER_UPDATED', name: 'Seller Updated' },
		{ value: 'INVOICE_DOCUMENT_ISSUED', name: 'Invoice Issued' },
		{ value: 'CREDIT_NOTE_ISSUED', name: 'Credit Note Issued' },
		{ value: 'EMAIL_ATTACHMENT_FAILED', name: 'Email Attachment Failed' },
		{ value: 'CLICKWRAP_UPDATED', name: 'Clickwrap Updated' },
		{ value: 'CLICKWRAP_DELETED', name: 'Clickwrap Deleted' },
		{ value: 'CLICKWRAP_ACCEPTED', name: 'Clickwrap Accepted' },
		{ value: 'PRODUCT_OPTION_UPDATED', name: 'Product Option Updated' },
		{ value: 'PRODUCT_OPTION_DELETED', name: 'Product Option Deleted' },
		{ value: 'PAYMENT_FAILED', name: 'Payment Failed' },
		{ value: 'PAYMENT_RECOVERED', name: 'Payment Recovered' },
		{ value: 'REFUND_ISSUED', name: 'Refund Issued' },
		{ value: 'REFUND_FAILED', name: 'Refund Failed' },
		{ value: 'CHARGEBACK_RECEIVED', name: 'Chargeback Received' },
		{ value: 'DISPUTE_CLOSED', name: 'Dispute Closed' },
		{ value: 'ENTITLEMENT_GRANTED', name: 'Entitlement Granted' },
		{ value: 'ENTITLEMENT_REVOKED', name: 'Entitlement Revoked' },
		{ value: 'MANUAL_ENTITLEMENT_GRANTED', name: 'Manual Entitlement Granted' },
		{ value: 'MANUAL_ENTITLEMENT_REVOKED', name: 'Manual Entitlement Revoked' },
		{ value: 'PRODUCT_CREATED', name: 'Product Created' },
		{ value: 'PRODUCT_UPDATED', name: 'Product Updated' },
		{ value: 'PRICE_CREATED', name: 'Price Created' },
		{ value: 'PRICE_ARCHIVED', name: 'Price Archived' },
		{ value: 'COUPON_CREATED', name: 'Coupon Created' },
		{ value: 'PRODUCT_FAMILY_CREATED', name: 'Product Family Created' },
		{ value: 'PRODUCT_FAMILY_UPDATED', name: 'Product Family Updated' },
		{ value: 'PRODUCT_FAMILY_ARCHIVED', name: 'Product Family Archived' },
		{ value: 'MEDIA_UPLOADED', name: 'Image Uploaded' },
		{ value: 'PRICE_UPDATED', name: 'Price Updated' },
		{ value: 'COUPON_UPDATED', name: 'Coupon Updated' },
		{ value: 'APPLICATION_REGISTERED', name: 'Application Registered' },
		{ value: 'APPLICATION_UPDATED', name: 'Application Updated' },
		{ value: 'API_KEY_ROTATED', name: 'API Key Rotated' },
		{ value: 'WEBHOOK_SECRET_ROTATED', name: 'Webhook Secret Rotated' },
		{ value: 'WEBHOOK_DELIVERY_DEAD', name: 'Webhook Delivery Dead' },
		{ value: 'WEBHOOK_DELIVERY_REPLAYED', name: 'Webhook Delivery Replayed' },
		{ value: 'WEBHOOK_DELIVERY_RETRIED', name: 'Webhook Delivery Retried' },
		{ value: 'API_KEY_AUTH_FAILED', name: 'API Key Auth Failed' },
		{ value: 'EMAIL_EXPIRED', name: 'Email Expired' },
		{ value: 'CATALOG_SYNCED', name: 'Catalogue Synced' },
		// §16. The anomaly is the per-finding row — which subscription, which session, which ref —
		// and the completion is the nightly summary the counts come from.
		{ value: 'RECONCILIATION_ANOMALY', name: 'Reconciliation Anomaly' },
		{ value: 'RECONCILIATION_COMPLETED', name: 'Reconciliation Completed' },
		{ value: 'MIGRATION_BATCH_STAGED', name: 'Migration Batch Staged' },
		{ value: 'MIGRATION_MAPPED', name: 'Migration Mapped' },
		{ value: 'MIGRATION_DRY_RUN', name: 'Migration Dry Run' },
		{ value: 'MIGRATION_APPLIED', name: 'Migration Applied' },
		{ value: 'MIGRATION_BATCH_DISCARDED', name: 'Migration Batch Discarded' },
		{ value: 'MIGRATION_DOCUMENTS_IMPORTED', name: 'Migration Documents Imported' },
		{ value: 'MIGRATION_BLOCKING_OVERRIDDEN', name: 'Migration Blocking Overridden' },
		{ value: 'MIGRATION_PROFILES_UNAVAILABLE', name: 'Migration Profiles Unavailable' },
		{ value: 'MIGRATION_REVERTED', name: 'Migration Reverted' },
		{ value: 'MIGRATION_APPLICATIONS_NOTIFIED', name: 'Migration Applications Notified' },
		{ value: 'MIGRATION_HISTORY_ATTACHED', name: 'Migration History Attached' }
	];

	const actionColors: Record<string, BadgeColor> = {
		'USER_REGISTERED': 'green',
		'USER_LOGIN': 'blue',
		'ROLE_CHANGED': 'yellow',
		'SETTINGS_CHANGED': 'purple',
		'USER_UPDATED': 'red',
		'PAYMENT_PROVIDER_CREATED': 'green',
		'PAYMENT_PROVIDER_UPDATED': 'purple',
		'PAYMENT_PROVIDER_DEFAULT_CHANGED': 'yellow',
		'PROVIDER_CREDENTIALS_ROTATED': 'red',
		'CURRENCY_UPDATED': 'blue',
		'REFUND_POLICY_UPDATED': 'blue',
		'EMAIL_PROVIDER_UPDATED': 'purple',
		'EMAIL_TEMPLATE_UPDATED': 'purple',
		'CHECKOUT_STARTED': 'blue',
		'PURCHASE_COMPLETED': 'green',
		'SUBSCRIPTION_CREATED': 'green',
		'SUBSCRIPTION_CHANGED': 'yellow',
		'SUBSCRIPTION_CANCELED': 'red',
		'SUBSCRIPTION_CANCEL_FAILED': 'red',
		'SUBSCRIPTION_RESUMED': 'green',
		'SUBSCRIPTION_TAX_RATE_UPDATED': 'yellow',
		'SELLER_UPDATED': 'blue',
		'INVOICE_DOCUMENT_ISSUED': 'green',
		'CREDIT_NOTE_ISSUED': 'yellow',
		'EMAIL_ATTACHMENT_FAILED': 'red',
		'CLICKWRAP_UPDATED': 'blue',
		'CLICKWRAP_DELETED': 'red',
		'CLICKWRAP_ACCEPTED': 'green',
		'PRODUCT_OPTION_UPDATED': 'blue',
		'PRODUCT_OPTION_DELETED': 'red',
		'PAYMENT_FAILED': 'red',
		'PAYMENT_RECOVERED': 'green',
		'REFUND_ISSUED': 'yellow',
		'REFUND_FAILED': 'red',
		'CHARGEBACK_RECEIVED': 'red',
		'DISPUTE_CLOSED': 'purple',
		'ENTITLEMENT_GRANTED': 'green',
		'ENTITLEMENT_REVOKED': 'red',
		'MANUAL_ENTITLEMENT_GRANTED': 'green',
		'MANUAL_ENTITLEMENT_REVOKED': 'red',
		'PRODUCT_CREATED': 'green',
		'PRODUCT_UPDATED': 'purple',
		'PRICE_CREATED': 'green',
		'PRICE_ARCHIVED': 'yellow',
		'COUPON_CREATED': 'green',
		'PRODUCT_FAMILY_CREATED': 'green',
		'PRODUCT_FAMILY_UPDATED': 'purple',
		'PRODUCT_FAMILY_ARCHIVED': 'red',
		'MEDIA_UPLOADED': 'blue',
		'PRICE_UPDATED': 'purple',
		'COUPON_UPDATED': 'purple',
		'APPLICATION_REGISTERED': 'green',
		'APPLICATION_UPDATED': 'purple',
		'API_KEY_ROTATED': 'red',
		'WEBHOOK_SECRET_ROTATED': 'red',
		'WEBHOOK_DELIVERY_DEAD': 'red',
		'WEBHOOK_DELIVERY_REPLAYED': 'blue',
		'WEBHOOK_DELIVERY_RETRIED': 'blue',
		'API_KEY_AUTH_FAILED': 'red',
		'EMAIL_EXPIRED': 'yellow',
		'CATALOG_SYNCED': 'blue',
		'RECONCILIATION_ANOMALY': 'yellow',
		'RECONCILIATION_COMPLETED': 'blue',
		'MIGRATION_BATCH_STAGED': 'blue',
		'MIGRATION_MAPPED': 'blue',
		'MIGRATION_DRY_RUN': 'blue',
		'MIGRATION_APPLIED': 'green',
		'MIGRATION_BATCH_DISCARDED': 'yellow',
		'MIGRATION_DOCUMENTS_IMPORTED': 'blue',
		'MIGRATION_BLOCKING_OVERRIDDEN': 'red',
		'MIGRATION_PROFILES_UNAVAILABLE': 'yellow',
		'MIGRATION_REVERTED': 'red',
		'MIGRATION_APPLICATIONS_NOTIFIED': 'blue',
		'MIGRATION_HISTORY_ATTACHED': 'green'
	};

	const actionIcons: Record<string, string> = {
		'USER_REGISTERED': 'fa-user-plus',
		'USER_LOGIN': 'fa-right-to-bracket',
		'ROLE_CHANGED': 'fa-user-shield',
		'SETTINGS_CHANGED': 'fa-gear',
		'USER_UPDATED': 'fa-user-pen',
		'PAYMENT_PROVIDER_CREATED': 'fa-credit-card',
		'PAYMENT_PROVIDER_UPDATED': 'fa-credit-card',
		'PAYMENT_PROVIDER_DEFAULT_CHANGED': 'fa-star',
		'PROVIDER_CREDENTIALS_ROTATED': 'fa-key',
		'CURRENCY_UPDATED': 'fa-coins',
		'REFUND_POLICY_UPDATED': 'fa-rotate-left',
		'EMAIL_PROVIDER_UPDATED': 'fa-envelope',
		'EMAIL_TEMPLATE_UPDATED': 'fa-envelope-open-text',
		'CHECKOUT_STARTED': 'fa-cart-shopping',
		'PURCHASE_COMPLETED': 'fa-circle-check',
		'SUBSCRIPTION_CREATED': 'fa-repeat',
		'SUBSCRIPTION_CHANGED': 'fa-arrow-right-arrow-left',
		'SUBSCRIPTION_CANCELED': 'fa-circle-xmark',
		'SUBSCRIPTION_CANCEL_FAILED': 'fa-triangle-exclamation',
		'SUBSCRIPTION_RESUMED': 'fa-play',
		'SUBSCRIPTION_TAX_RATE_UPDATED': 'fa-percent',
		'SELLER_UPDATED': 'fa-building',
		'INVOICE_DOCUMENT_ISSUED': 'fa-file-invoice',
		'CREDIT_NOTE_ISSUED': 'fa-file-circle-minus',
		'EMAIL_ATTACHMENT_FAILED': 'fa-paperclip',
		'CLICKWRAP_UPDATED': 'fa-file-signature',
		'CLICKWRAP_DELETED': 'fa-trash',
		'CLICKWRAP_ACCEPTED': 'fa-square-check',
		'PRODUCT_OPTION_UPDATED': 'fa-sliders',
		'PRODUCT_OPTION_DELETED': 'fa-trash',
		'PAYMENT_FAILED': 'fa-triangle-exclamation',
		'PAYMENT_RECOVERED': 'fa-arrow-rotate-left',
		'REFUND_ISSUED': 'fa-money-bill-transfer',
		'REFUND_FAILED': 'fa-circle-exclamation',
		'CHARGEBACK_RECEIVED': 'fa-gavel',
		'DISPUTE_CLOSED': 'fa-scale-balanced',
		'ENTITLEMENT_GRANTED': 'fa-unlock',
		'ENTITLEMENT_REVOKED': 'fa-lock',
		'MANUAL_ENTITLEMENT_GRANTED': 'fa-hand-holding-heart',
		'MANUAL_ENTITLEMENT_REVOKED': 'fa-hand',
		'PRODUCT_CREATED': 'fa-box',
		'PRODUCT_UPDATED': 'fa-box-open',
		'PRICE_CREATED': 'fa-tag',
		'PRICE_ARCHIVED': 'fa-box-archive',
		'COUPON_CREATED': 'fa-ticket',
		'PRODUCT_FAMILY_CREATED': 'fa-layer-group',
		'PRODUCT_FAMILY_UPDATED': 'fa-layer-group',
		'PRODUCT_FAMILY_ARCHIVED': 'fa-box-archive',
		'MEDIA_UPLOADED': 'fa-image',
		'PRICE_UPDATED': 'fa-tags',
		'COUPON_UPDATED': 'fa-ticket-simple',
		'APPLICATION_REGISTERED': 'fa-plug',
		'APPLICATION_UPDATED': 'fa-plug-circle-bolt',
		'API_KEY_ROTATED': 'fa-key',
		'WEBHOOK_SECRET_ROTATED': 'fa-shield-halved',
		'WEBHOOK_DELIVERY_DEAD': 'fa-skull-crossbones',
		'WEBHOOK_DELIVERY_REPLAYED': 'fa-rotate',
		'WEBHOOK_DELIVERY_RETRIED': 'fa-arrow-rotate-right',
		'API_KEY_AUTH_FAILED': 'fa-key',
		'EMAIL_EXPIRED': 'fa-envelope-circle-check',
		'CATALOG_SYNCED': 'fa-arrows-rotate',
		'RECONCILIATION_ANOMALY': 'fa-triangle-exclamation',
		'RECONCILIATION_COMPLETED': 'fa-clipboard-check',
		'MIGRATION_BATCH_STAGED': 'fa-file-import',
		'MIGRATION_MAPPED': 'fa-diagram-project',
		'MIGRATION_DRY_RUN': 'fa-flask',
		'MIGRATION_APPLIED': 'fa-database',
		'MIGRATION_BATCH_DISCARDED': 'fa-trash',
		'MIGRATION_DOCUMENTS_IMPORTED': 'fa-file-pdf',
		'MIGRATION_BLOCKING_OVERRIDDEN': 'fa-triangle-exclamation',
		'MIGRATION_PROFILES_UNAVAILABLE': 'fa-address-card',
		'MIGRATION_REVERTED': 'fa-rotate-left',
		'MIGRATION_APPLICATIONS_NOTIFIED': 'fa-tower-broadcast',
		'MIGRATION_HISTORY_ATTACHED': 'fa-link'
	};

	onMount(async () => {
		await loadLogs();
	});

	async function loadLogs() {
		loading = true;
		error = '';

		try {
			const offset = (page - 1) * limit;
			const params = new URLSearchParams({
				limit: limit.toString(),
				offset: offset.toString()
			});
			
			if (actionFilter) {
				params.append('action', actionFilter);
			}

			const response = await AbstractID.authenticatedFetch(`${site.backend.api.auditList}?${params}`);
			
			if (response.ok) {
				const data = await response.json();
				logs = data.items || [];
				total = data.total || 0;
			} else {
				throw new Error('Failed to fetch audit logs');
			}
		} catch (err: unknown) {
			error = err instanceof Error ? err.message : 'Failed to load audit logs';
			console.error('Audit logs error:', err);
		} finally {
			loading = false;
		}
	}

	function handleFilterChange() {
		page = 1;
		loadLogs();
	}

	function handlePageChange(newPage: number) {
		page = newPage;
		loadLogs();
	}

	function parseDetails(details: unknown) {
		if (!details) return null;
		try {
			return typeof details === 'string' ? JSON.parse(details) : details;
		} catch {
			return details;
		}
	}

	/**
	 * Which rows are open, keyed by entry id rather than by index.
	 *
	 * By index, paging or a refresh would leave the *position* open and reveal a different entry's
	 * details under a row somebody had opened deliberately — on the one screen whose whole purpose
	 * is saying which record a fact belongs to.
	 */
	let expandedIDs: Record<number, boolean> = {};

	function fnToggle(id: number): void {
		expandedIDs = { ...expandedIDs, [id]: expandedIDs[id] !== true };
	}

	/**
	 * What to show in an actor column.
	 *
	 * The name when the server could resolve one, and the **id** otherwise — never a blank and
	 * never the word "unknown". A deleted user still has to be identifiable across entries, which
	 * is the whole reason `userID` carries no foreign key (§15): the trail outlives the users in
	 * it, and `User #12` is what ties three of its rows to one actor after the row is gone.
	 * No user at all is the store acting on its own, which is `System` rather than `User #0`.
	 */
	function actorName(id: number | undefined, name: string | null | undefined): string {
		if (name !== null && name !== undefined && name.length > 0) return name;
		return typeof id === 'number' && id > 0 ? `User #${id}` : 'System';
	}

	/** `previousValue` → `Previous value`. The key is the label; nothing here is hand-written. */
	function labelOf(key: string): string {
		const spaced = key.replace(/[._]/g, ' ').replace(/([a-z0-9])([A-Z])/g, '$1 $2');
		return spaced.charAt(0).toUpperCase() + spaced.slice(1);
	}

	/**
	 * One detail value, as text.
	 *
	 * `null` and `undefined` render as an em dash rather than as the words "null" and "undefined":
	 * every `SETTINGS_CHANGED` for a key that had no override carries `previousValue: null`, and a
	 * column of the literal word `null` is what made this screen read as broken rather than as
	 * empty. An empty string is stated as such, because on this screen "" and "not set" are
	 * genuinely different facts about what a setting held.
	 */
	function valueText(value: unknown): string {
		if (value === null || value === undefined) return '—';
		if (value === '') return '(empty)';
		if (typeof value === 'boolean') return value ? 'true' : 'false';
		if (typeof value === 'object') return JSON.stringify(value, null, 2);
		return String(value);
	}

	/** Whether a value wants a block of its own rather than a line beside its label. */
	function isBlockValue(value: unknown): boolean {
		return typeof value === 'object' && value !== null;
	}

	interface DetailEntry {
		key: string;
		label: string;
		text: string;
		isBlock: boolean;
	}

	/**
	 * The details object as label/value rows, **in the order the code wrote them**.
	 *
	 * Deliberately generic: there are sixty-odd actions and each shapes `details` for itself, so a
	 * per-action renderer here would be sixty branches that fall behind the day somebody adds the
	 * sixty-first — and §15's rule is that a new action costs the four lists it already costs, not
	 * a screen. Insertion order is what `AuditService.log` callers chose, so `SETTINGS_CHANGED`
	 * leads with its key and `RECONCILIATION_ANOMALY` with its kind, which is what somebody
	 * scanning the column is looking for.
	 */
	function detailEntries(details: unknown): DetailEntry[] {
		const parsed = parseDetails(details);
		if (parsed === null || typeof parsed !== 'object') {
			return parsed === null ? [] : [{ key: 'value', label: 'Value', text: String(parsed), isBlock: false }];
		}
		return Object.entries(parsed as Record<string, unknown>).map(([key, value]) => ({
			key,
			label: labelOf(key),
			text: valueText(value),
			isBlock: isBlockValue(value)
		}));
	}

	const PREVIEW_ENTRIES = 2;
	const PREVIEW_VALUE_CHARS = 32;

	/** A value shortened for the closed row, per value — never by cutting the whole JSON string. */
	function previewText(text: string): string {
		const oneLine = text.replace(/\s+/g, ' ').trim();
		return oneLine.length > PREVIEW_VALUE_CHARS
			? `${oneLine.slice(0, PREVIEW_VALUE_CHARS - 1)}…`
			: oneLine;
	}

	$: totalPages = Math.ceil(total / limit);
	$: filteredLogs = searchQuery 
		? logs.filter(log => 
			log.action.toLowerCase().includes(searchQuery.toLowerCase()) ||
			log.ipAddress?.toLowerCase().includes(searchQuery.toLowerCase())
		)
		: logs;
</script>

<PageHeader title="Audit logs">
	<svelte:fragment slot="description">
		Every recorded action, filterable by what it was and by who performed it.
	</svelte:fragment>
</PageHeader>

<div class="space-y-6">
	<!-- Filters -->
	<Panel title="Entries" faIcon="list-check">
		<div class="controlpanel !gap-x-4 py-2">
			<div class="flex-1 min-w-[200px]">
				<Search placeholder="Search logs..." bind:value={searchQuery} />
			</div>

			<div class="w-48">
				<SelectField
					items={actionTypes}
					bind:value={actionFilter}
					on:change={handleFilterChange}
					placeholder="Filter by action"
				/>
			</div>

			<Button color="alternative" on:click={loadLogs} disabled={loading}>
				{#if loading}
					<i class="fa-solid fa-spinner fa-spin me-2"></i>
				{:else}
					<i class="fa-solid fa-refresh me-2"></i>
				{/if}
				Refresh
			</Button>
		</div>
	</Panel>

	{#if error}
		<div class="p-4 bg-red-50 dark:bg-red-900/20 rounded-2xl text-red-700 dark:text-red-300">
			<i class="fa-solid fa-exclamation-triangle me-2"></i>
			{error}
		</div>
	{/if}

	<!-- Logs Table -->
	<div class="widget p-1">
		{#if loading}
			<div class="p-8 text-center">
				<i class="fa-solid fa-spinner fa-spin text-2xl text-gray-400 mb-2"></i>
				<p class="text-default">Loading audit logs...</p>
			</div>
		{:else if filteredLogs.length === 0}
			<div class="p-8 text-center">
				<i class="fa-solid fa-clipboard-list text-4xl text-gray-300 dark:text-gray-600 mb-4"></i>
				<p class="text-default">
					{searchQuery || actionFilter ? 'No logs match your filters' : 'No audit logs yet'}
				</p>
			</div>
		{:else}
			<Table striped={true} divClass="dataTable relative overflow-x-auto rounded-2xl">
				<TableHead class="bg-depth">
					<!--
						No fixed widths bar the chevron's. `w-32` on two columns of people's names and
						`w-40` on a column of badges were narrower than their own contents, so every
						row wrapped onto two lines — including the headings — while the table had room
						to spare. Details takes `w-full` instead: one cell claiming the slack sizes
						every other column to its content, which is what a table of short values wants
						and what leaves the long one somewhere to grow.

						Headings that are two words carry the nowrap, for the reason the cells below
						do: `.dataTable th` sets `white-space: normal` with one class plus an element,
						so a `whitespace-nowrap` utility on the cell loses to it and only an inner
						element can opt out.
					-->
					<TableHeadCell class="w-8"><span class="sr-only">Expand</span></TableHeadCell>
					<TableHeadCell>Time</TableHeadCell>
					<TableHeadCell>Action</TableHeadCell>
					<TableHeadCell><span class="whitespace-nowrap">Performed by</span></TableHeadCell>
					<!--
						No Target column. It is sparse rather than empty — most actions are performed on
						the store itself and name nobody, while a role change, a manual grant and a
						revocation all do — so as a column it spent a sixth of the width to say "—" on
						most rows. The fact moves into the expanded row, where it sits beside the actor
						it only means anything next to.
					-->
					<TableHeadCell class="w-full">Details</TableHeadCell>
				</TableHead>
				<TableBody>
					{#each filteredLogs as log (log.id)}
						{@const entries = detailEntries(log.details)}
						{@const isExpanded = expandedIDs[log.id] === true}
						<!--
							The whole row opens the entry, because the useful target is the row somebody
							is already reading rather than a caret at the end of it. The chevron is a
							real button all the same: it is what carries `aria-expanded` and what a
							keyboard reaches, and `TableBodyRow` forwards click but not keydown.
						-->
						<TableBodyRow class="cursor-pointer" on:click={() => fnToggle(log.id)}>
							<TableBodyCell>
								<button
									type="button"
									class="p-1 text-default hover:text-primary-500"
									aria-expanded={isExpanded}
									aria-controls={`audit-details-${log.id}`}
									aria-label={`${isExpanded ? 'Collapse' : 'Expand'} entry ${log.id}`}
									on:click|stopPropagation={() => fnToggle(log.id)}
								>
									<i class={`fa-solid ${isExpanded ? 'fa-angle-down' : 'fa-angle-right'}`}></i>
								</button>
							</TableBodyCell>
							<TableBodyCell>
								<!--
									Two lines on purpose — "2d ago" over the date it stands for — so each of
									them has to be one line. The nowrap sits on the spans rather than on the
									cell because `.dataTable td` outranks the utility.
								-->
								<div class="flex flex-col">
									<span class="text-sm font-medium text-gray-900 dark:text-white whitespace-nowrap">
										{formatRelativeDate(log.creationDate)}
									</span>
									<span class="text-xs text-default whitespace-nowrap">
										{formatFriendlyDate(log.creationDate)}
									</span>
								</div>
							</TableBodyCell>
							<TableBodyCell>
								<Badge color={(actionColors[log.action] || 'dark')} class="whitespace-nowrap">
									<i class="fa-solid {actionIcons[log.action] || 'fa-circle'} me-1"></i>
									{log.action.replace(/_/g, ' ')}
								</Badge>
							</TableBodyCell>
							<!--
								A person's name is one value rather than prose, so it opts out of the
								table's wrapping exactly as a money column does — "Rogiel / Sulzbach"
								across two lines in every row is the same fault as "€58.0 / 0", and it
								doubled the height of the whole table.
							-->
							<TableBodyCell>
								<span class="text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
									{actorName(log.userID, log.userName)}
								</span>
							</TableBodyCell>

							<TableBodyCell>
								<!--
									Named values, not a cut-off JSON string. The old cell rendered
									`JSON.stringify` truncated to 200px, so every settings change read
									`{"key":"store.publicURL","previousVal…` — the braces and quoting spent
									the width, and the one field somebody wanted was past the ellipsis.
								-->
								{#if entries.length === 0}
									<span class="text-sm text-gray-400">—</span>
								{:else}
									<div class="flex flex-wrap items-baseline gap-x-3 gap-y-1">
										{#each entries.slice(0, PREVIEW_ENTRIES) as entry (entry.key)}
											<!--
												The pair stays together: a label stranded at the end of one line
												above its value reads as two separate facts. The value is already
												cut to `PREVIEW_VALUE_CHARS`, so nowrap here cannot overflow the
												column — the wrapping that matters happens *between* pairs.
											-->
											<span class="text-xs whitespace-nowrap">
												<span class="text-default">{entry.label}</span>
												<span class="font-medium tabular-nums">{previewText(entry.text)}</span>
											</span>
										{/each}
										{#if entries.length > PREVIEW_ENTRIES}
											<!-- Stated rather than implied: a row with more behind it must not look
											     like a row that is complete. -->
											<span class="text-xs text-default">+{entries.length - PREVIEW_ENTRIES} more</span>
										{/if}
									</div>
								{/if}
							</TableBodyCell>
						</TableBodyRow>

						{#if isExpanded}
							<!--
								The whole entry. It carries the fields the closed row shortens or omits —
								the absolute timestamp, the entry id, the IP — because "which record is
								this and when exactly" is the question an audit trail is opened to answer,
								and a relative "3 hours ago" cannot answer it.
							-->
							<TableBodyRow>
								<TableBodyCell colspan={5} class="!p-0">
									<div id={`audit-details-${log.id}`} class="bg-depth p-4 space-y-4">
										<dl class="grid gap-x-6 gap-y-2 sm:grid-cols-2 lg:grid-cols-4 text-xs">
											<div>
												<dt class="text-default">Recorded</dt>
												<dd class="font-medium">{formatFriendlyDate(log.creationDate)}</dd>
											</div>
											<div>
												<dt class="text-default">Entry</dt>
												<dd class="font-medium tabular-nums">#{log.id}</dd>
											</div>
											<div>
												<dt class="text-default">Performed by</dt>
												<!-- The id as well as the name: the name is what a reader recognises,
												     the id is what the next entry can be matched on. -->
												<dd class="font-medium">
													{actorName(log.userID, log.userName)}
													{#if log.userName && log.userID}<span class="text-default">#{log.userID}</span>{/if}
												</dd>
											</div>
											<div>
												<!--
													Only where there is one: "Performed on — nobody" is noise on the
													majority of actions, which are performed on the store itself.
												-->
												<dt class="text-default">Performed on</dt>
												<dd class="font-medium">
													{#if log.targetUserID}
														{actorName(log.targetUserID, log.targetUserName)}
														{#if log.targetUserName}<span class="text-default">#{log.targetUserID}</span>{/if}
													{:else}
														—
													{/if}
												</dd>
											</div>
											<div>
												<dt class="text-default">IP address</dt>
												<dd class="font-medium">{log.ipAddress || '—'}</dd>
											</div>
										</dl>

										{#if entries.length === 0}
											<p class="text-xs text-default">This action recorded no further detail.</p>
										{:else}
											<dl class="space-y-2 text-xs">
												{#each entries as entry (entry.key)}
													<div class={entry.isBlock ? '' : 'flex flex-wrap items-baseline gap-x-3'}>
														<dt class="text-default {entry.isBlock ? 'mb-1' : 'min-w-[9rem]'}">{entry.label}</dt>
														{#if entry.isBlock}
															<!-- Pretty-printed and wrapped: a nested payload on one line is the
															     unreadable thing this screen was already doing. -->
															<dd>
																<pre class="bg-depth rounded p-2 overflow-x-auto whitespace-pre-wrap [overflow-wrap:anywhere] font-mono">{entry.text}</pre>
															</dd>
														{:else}
															<dd class="font-medium [overflow-wrap:anywhere]">{entry.text}</dd>
														{/if}
													</div>
												{/each}
											</dl>
										{/if}
									</div>
								</TableBodyCell>
							</TableBodyRow>
						{/if}
					{/each}
				</TableBody>
			</Table>

			<!-- Pagination -->
			{#if totalPages > 1}
				<div class="flex items-center justify-between p-4 border-t border-gray-200 dark:border-gray-700">
					<p class="text-sm text-default">
						Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of {total} entries
					</p>

					<div class="flex items-center gap-2">
						<Button
							color="alternative"
							size="sm"
							disabled={page <= 1}
							on:click={() => handlePageChange(page - 1)}
						>
							<i class="fa-solid fa-chevron-left me-1"></i>
							Previous
						</Button>

						<span class="text-sm text-default px-3">
							Page {page} of {totalPages}
						</span>

						<Button
							color="alternative"
							size="sm"
							disabled={page >= totalPages}
							on:click={() => handlePageChange(page + 1)}
						>
							Next
							<i class="fa-solid fa-chevron-right ms-1"></i>
						</Button>
					</div>
				</div>
			{/if}
		{/if}
	</div>

	<!-- Legend -->
	<Panel title="Action Types" faIcon="tags" compact>
		<div class="flex flex-wrap gap-3">
			{#each actionTypes.slice(1) as action}
				<Badge color={(actionColors[action.value] || 'dark')}>
					<i class="fa-solid {actionIcons[action.value] || 'fa-circle'} me-1"></i>
					{action.name}
				</Badge>
			{/each}
		</div>
	</Panel>
</div>
